import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Camera,
  Calendar,
  Heart,
  HeartPulse,
  Activity,
  Thermometer,
  Wind,
  Scale,
  Pill,
  FileText,
  FlaskConical,
  Stethoscope,
  Send,
  Award,
  ArrowUpRight,
  AlertCircle,
  Clock,
  Building2,
  Droplet,
  Download,
  Play,
  User as UserIcon,
} from "@/components/icons";
import { calcAge, fmtDate, fmtTime } from "./common";
import api from "../../../lib/api";
import { generateTimelinePdf } from "../../../lib/patientTimelinePdf";
import { useAuth } from "../../../lib/auth";
import AppleKpi from "../../../components/AppleKpi";
import FancyKpi, { SparkBars, SparkLine, SparkPill, SparkArea, SparkCandles, SparkDotGrid } from "../../../components/FancyKpi";

const GENDER_LABEL = { M: "Masculino", F: "Femenino", O: "Otro" };

/**
 * OverviewTab — Rediseño completo (Feb 2026).
 *
 * Estructura Apple-minimalist:
 *   1. Ficha compacta superior (foto · nombre · datos personales · alergias chip)
 *   2. KPIs reales (sin sparklines falsos, sin %fake): Consultas · Recetas ·
 *      Diagnósticos · Próxima cita.
 *   3. Grid 2 columnas:
 *      - Último signos vitales (si existe)
 *      - Alergias + Condiciones (tags reales desde patient.allergies/notes)
 *   4. Timeline cronológico (eventos reales mezclados: citas, recetas,
 *      diagnósticos, laboratorios).
 */
export default function OverviewTab({
  patient,
  diagnoses,
  appointments,
  prescriptions,
  latestDx,
  latestRx,
  nextAppt,
  v,
  fileRef,
  isDoctor,
  onAddVitals,
  onStartConsult,
  startingConsult,
  onCreateRx,
  onCreateCert,
  onCreateReferral,
}) {
  const { user } = useAuth();
  const [labs, setLabs] = useState([]);

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    api
      .get(`/lab-results?patient_id=${patient.id}`)
      .then((r) => {
        if (!cancelled) setLabs(r.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  // ── KPIs reales ─────────────────────────────────────────────
  const totalConsults = appointments.filter(
    (a) => a.status === "completed"
  ).length;
  const totalRx = prescriptions.length;
  const totalDx = diagnoses.length;
  const upcomingAppt = nextAppt;

  // ── Series mensuales para mini-charts (últimos 12 meses) ────
  const monthlySeries = useMemo(() => {
    const buckets = Array.from({ length: 12 }, () => ({ c: 0, r: 0, d: 0 }));
    const now = new Date();
    const base = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const monthIdx = (iso) => {
      if (!iso) return -1;
      const d = new Date(iso);
      if (isNaN(d.getTime())) return -1;
      const diff = (d.getFullYear() - base.getFullYear()) * 12 + (d.getMonth() - base.getMonth());
      return diff >= 0 && diff < 12 ? diff : -1;
    };
    appointments
      .filter((a) => a.status === "completed")
      .forEach((a) => {
        const i = monthIdx(a.date);
        if (i >= 0) buckets[i].c += 1;
      });
    prescriptions.forEach((p) => {
      const i = monthIdx(p.created_at);
      if (i >= 0) buckets[i].r += 1;
    });
    diagnoses.forEach((d) => {
      const i = monthIdx(d.created_at);
      if (i >= 0) buckets[i].d += 1;
    });
    return {
      consults: buckets.map((b) => b.c),
      rx: buckets.map((b) => b.r),
      dx: buckets.map((b) => b.d),
    };
  }, [appointments, prescriptions, diagnoses]);

  // ── Historial de vitales para los mini-charts ────────────────
  //   Cada diagnóstico guarda `vitals` embebido + created_at. Reconstruimos
  //   series cronológicas por campo. Para presión arterial usamos la
  //   sistólica (número antes del "/") para graficar.
  const vitalsSeries = useMemo(() => {
    const parseNum = (x) => {
      if (x === null || x === undefined || x === "") return null;
      const s = String(x).replace(",", ".");
      const m = s.match(/-?\d+(\.\d+)?/);
      return m ? parseFloat(m[0]) : null;
    };
    const parseBP = (x) => {
      if (!x) return null;
      const m = String(x).match(/(\d+)\s*\/\s*(\d+)/);
      return m ? parseFloat(m[1]) : null;
    };
    const ordered = [...diagnoses]
      .filter((d) => d.vitals)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const pick = (fn) => ordered.map((d) => fn(d.vitals)).filter((n) => n !== null);
    return {
      bp:   pick((v) => parseBP(v.blood_pressure)),
      hr:   pick((v) => parseNum(v.heart_rate)),
      temp: pick((v) => parseNum(v.temperature)),
      spo2: pick((v) => parseNum(v.oxygen_saturation ?? v.spo2)),
      rr:   pick((v) => parseNum(v.respiration_rate ?? v.respiratory_rate)),
      wt:   pick((v) => parseNum(v.weight)),
    };
  }, [diagnoses]);

  // Timestamp relativo "hace X días/meses"
  const relTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    const h = Math.floor(diffMs / 36e5);
    if (h < 1) return "hace minutos";
    if (h < 24) return `hace ${h} h`;
    const days = Math.floor(h / 24);
    if (days < 30) return `hace ${days} d`;
    const months = Math.floor(days / 30);
    if (months < 12) return `hace ${months} m`;
    return `hace ${Math.floor(months / 12)} año(s)`;
  };
  const vitalsAgo = relTime(latestDx?.created_at);

  // Pills de rango clínico simple (adulto, sin estratificar por edad/sexo
  // porque no quiero mostrar falsos alarmas sin lógica médica real).
  const bpPill = (() => {
    const bp = v?.blood_pressure;
    if (!bp) return null;
    const m = String(bp).match(/(\d+)\s*\/\s*(\d+)/);
    if (!m) return null;
    const sys = +m[1];
    const dia = +m[2];
    if (sys >= 140 || dia >= 90) return "Alta";
    if (sys < 90 || dia < 60) return "Baja";
    return "Normal";
  })();
  const hrPill = (() => {
    const n = parseFloat(v?.heart_rate);
    if (!isFinite(n)) return null;
    if (n > 100) return "Taquicardia";
    if (n < 60) return "Bradicardia";
    return "Reposo";
  })();
  const tempPill = (() => {
    const n = parseFloat(String(v?.temperature || "").replace(",", "."));
    if (!isFinite(n)) return null;
    if (n >= 38) return "Fiebre";
    if (n < 36) return "Baja";
    return "Normal";
  })();
  const spo2Pill = (() => {
    const n = parseFloat(v?.oxygen_saturation);
    if (!isFinite(n)) return null;
    if (n < 95) return "Baja";
    return "Normal";
  })();
  const rrPill = (() => {
    const n = parseFloat(v?.respiration_rate);
    if (!isFinite(n)) return null;
    if (n > 20) return "Elevada";
    if (n < 12) return "Baja";
    return "Normal";
  })();

  // ── Timeline (mezcla cronológica descendente) ───────────────
  const timeline = useMemo(() => {
    const items = [
      ...appointments.map((a) => ({
        id: `ap-${a.id}`,
        type: "appt",
        date: a.date,
        title:
          a.status === "completed"
            ? "Consulta realizada"
            : a.status === "cancelled"
              ? "Cita cancelada"
              : "Cita programada",
        subtitle: a.reason || "—",
        meta: a.status,
      })),
      ...prescriptions.map((p) => ({
        id: `rx-${p.id}`,
        type: "rx",
        date: p.created_at,
        title: "Receta emitida",
        subtitle:
          Array.isArray(p.medications) && p.medications.length
            ? p.medications
                .map((m) => m.name || m.drug || "")
                .filter(Boolean)
                .slice(0, 3)
                .join(" · ") || "—"
            : p.diagnosis || "—",
      })),
      ...diagnoses.map((d) => ({
        id: `dx-${d.id}`,
        type: "dx",
        date: d.created_at,
        title: "Diagnóstico",
        subtitle: d.diagnosis || d.notes || "—",
      })),
      ...labs.map((l) => ({
        id: `lb-${l.id}`,
        type: "lab",
        date: l.created_at || l.date,
        title: "Resultado de laboratorio",
        subtitle: l.name || l.notes || "—",
      })),
    ]
      .filter((e) => e.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return items;
  }, [appointments, prescriptions, diagnoses, labs]);

  // ── Alergias + Condiciones (parse CSV o string) ─────────────
  const allergies = (patient?.allergies || "")
    .split(/[,;·]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const conditions = (patient?.notes || "")
    .split(/[,;·\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const hasVitals =
    v && (v.blood_pressure || v.heart_rate || v.temperature || v.weight || v.height || v.oxygen_saturation || v.respiration_rate);

  return (
    <div className="space-y-3" data-testid="patient-overview">
      {/* ───── 2. Signos vitales (6 KPI cards) + Historial médico lateral ───── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 items-start">
        <div className="space-y-2">
          <div className="flex items-center justify-between pt-1">
            <h3 className="text-[15px] font-semibold text-black tracking-tight">
              Signos vitales
            </h3>
            {isDoctor && (
              <button
                onClick={onAddVitals}
                data-testid="vitals-add-btn"
                className="text-[12px] font-semibold text-[#0071E3] hover:underline"
              >
                + Añadir
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            <FancyKpi
              title="Presión arterial"
              icon={Heart}
              color="red"
              value={v?.blood_pressure || "—"}
              pill={bpPill || "mmHg"}
              timestamp={vitalsAgo || "Sin registros"}
              chart={<SparkArea data={vitalsSeries.bp} color="#F05C62" />}
              axisLeft="histórico"
              axisRight="hoy"
              testid="vital-bp"
            />
            <FancyKpi
              title="Pulso"
              icon={HeartPulse}
              color="orange"
              value={v?.heart_rate ? `${v.heart_rate} bpm` : "—"}
              pill={hrPill || "en reposo"}
              timestamp={vitalsAgo || "Sin registros"}
              chart={<SparkCandles data={vitalsSeries.hr} color="#F58A4B" />}
              axisLeft="00.00"
              axisRight="24.00"
              testid="vital-hr"
            />
            <FancyKpi
              title="Temperatura"
              icon={Thermometer}
              color="violet"
              value={v?.temperature ? `${v.temperature}°C` : "—"}
              pill={tempPill || "°C"}
              timestamp={vitalsAgo || "Sin registros"}
              chart={<SparkDotGrid value={Math.min(vitalsSeries.temp.length, 10)} total={10} color="#8B7CFF" />}
              testid="vital-temp"
            />
            <FancyKpi
              title="Saturación O₂"
              icon={Activity}
              color="green"
              value={v?.oxygen_saturation ? `${v.oxygen_saturation}%` : "—"}
              pill={spo2Pill || "%"}
              timestamp={vitalsAgo || "Sin registros"}
              chart={<SparkBars data={vitalsSeries.spo2} color="#2BC48A" />}
              axisLeft="0"
              axisRight="100"
              testid="vital-spo2"
            />
            <FancyKpi
              title="Respiración"
              icon={Wind}
              color="blue"
              value={v?.respiration_rate ? `${v.respiration_rate} rpm` : "—"}
              pill={rrPill || "rpm"}
              timestamp={vitalsAgo || "Sin registros"}
              chart={<SparkCandles data={vitalsSeries.rr} color="#5E6AE8" />}
              axisLeft="histórico"
              axisRight="hoy"
              testid="vital-rr"
            />
            <FancyKpi
              title="Peso"
              icon={Scale}
              color="purple"
              value={v?.weight ? `${v.weight} kg` : "—"}
              pill={v?.weight ? "Registrado" : "kg"}
              timestamp={vitalsAgo || "Sin registros"}
              chart={<SparkLine data={vitalsSeries.wt} stroke="#B661F2" dots={true} />}
              axisLeft="histórico"
              axisRight="hoy"
              testid="vital-weight"
            />
          </div>
        </div>

        {/* Historial médico — sidebar derecha */}
        <div
          className="bg-[#F5F5F7] rounded-[24px] p-5 lg:sticky lg:top-[72px]"
          data-testid="patient-conditions-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <FileText
              className="w-4 h-4 text-black/60"
              strokeWidth={1.75}
              weight="regular"
            />
            <div className="text-[13px] font-semibold text-black">
              Historial médico
            </div>
          </div>
          {conditions.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {conditions.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex h-7 items-center px-2.5 rounded-md bg-white text-[12px] font-semibold text-black/80 border border-black/[0.04]"
                >
                  {c}
                </span>
              ))}
            </div>
          ) : (
            <EmptyMini
              icon={FileText}
              text="Sin condiciones registradas"
            />
          )}

          {/* Responsable / tutor */}
          {patient.guardian_name && (
            <div className="mt-4 pt-3 border-t border-black/[0.05]">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1">
                Responsable / Tutor
              </div>
              <div className="text-[13px] font-semibold text-black">
                {patient.guardian_name}
                {patient.guardian_role && (
                  <span className="text-black/50 font-normal"> · {patient.guardian_role}</span>
                )}
              </div>
              {patient.guardian_phone && (
                <div className="text-[12px] text-black/55 mt-0.5">
                  <a
                    href={`tel:${patient.guardian_phone}`}
                    className="hover:text-[#0071E3]"
                  >
                    {patient.guardian_phone}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ───── 4. TIMELINE ───── */}
      <div
        className="bg-[#F5F5F7] rounded-[24px] p-5"
        data-testid="patient-timeline"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock
              className="w-4 h-4 text-black/60"
              strokeWidth={1.75}
              weight="regular"
            />
            <div className="text-[13px] font-semibold text-black">
              Timeline del paciente
            </div>
          </div>
          <div className="flex items-center gap-2">
            {timeline.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  generateTimelinePdf({
                    patient,
                    timeline,
                    doctor: { name: user?.name },
                  })
                }
                data-testid="timeline-export-pdf"
                title="Descargar timeline en PDF"
                className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-white hover:bg-black/[0.03] text-[11px] font-semibold text-black/70 transition"
              >
                <Download className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                Exportar PDF
              </button>
            )}
            <div className="text-[11px] text-black/45">
              {timeline.length} evento{timeline.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {timeline.length === 0 ? (
          <EmptyMini icon={Clock} text="Aún no hay actividad registrada" />
        ) : (
          <div className="relative pl-7">
            <div className="absolute left-[13px] top-0 bottom-0 w-px bg-black/[0.08]" />
            <div className="space-y-3">
              {timeline.slice(0, 10).map((e) => (
                <TimelineRow key={e.id} event={e} />
              ))}
            </div>
            {timeline.length > 10 && (
              <div className="mt-3 pl-0 text-center">
                <span className="text-[11px] text-black/45">
                  Mostrando los 10 eventos más recientes · ver pestañas para más detalle
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function ContactItem({ icon: Icon, value, href }) {
  if (!value) {
    return (
      <div className="flex items-center gap-2 text-black/35 min-w-0">
        <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} weight="regular" />
        <span className="text-[12.5px]">—</span>
      </div>
    );
  }
  const content = (
    <div className="flex items-center gap-2 min-w-0 text-black/70">
      <Icon
        className="w-3.5 h-3.5 flex-shrink-0 text-black/40"
        strokeWidth={1.75}
        weight="regular"
      />
      <span className="truncate">{value}</span>
    </div>
  );
  return href ? (
    <a href={href} className="hover:text-[#0071E3] transition-colors">
      {content}
    </a>
  ) : (
    content
  );
}

function Kpi({ icon: Icon, label, value, hint, testid, valueClass = "" }) {
  return (
    <div
      className="rounded-[24px] bg-[#F5F5F7] p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center">
          <Icon className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
        </div>
      </div>
      <div className={`mt-3 font-semibold text-black tracking-tight ${valueClass || "text-[28px]"} leading-tight`}>
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-widest font-semibold text-black/45 mt-1">
        {label}
      </div>
      {hint && (
        <div className="text-[11.5px] text-black/50 mt-2 truncate">{hint}</div>
      )}
    </div>
  );
}

function VitalMini({ label, value, unit }) {
  const show = value !== undefined && value !== null && value !== "";
  return (
    <div className="rounded-xl bg-white px-3 py-2.5 min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
        {label}
      </div>
      <div className="text-[14px] font-semibold text-black mt-0.5 truncate">
        {show ? (
          <>
            {value}
            {unit && <span className="text-black/40 font-normal text-[11px] ml-0.5">{unit}</span>}
          </>
        ) : (
          <span className="text-black/25">—</span>
        )}
      </div>
    </div>
  );
}

/**
 * VitalCell — Celda tipo "Signos vitales" (ver screenshot de referencia).
 * Layout: label arriba-izquierda, valor grande centrado, unit abajo-derecha.
 */
function VitalCell({ label, value, unit, testid }) {
  const show = value !== undefined && value !== null && value !== "";
  return (
    <div
      className="relative min-h-[72px] flex flex-col justify-between"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-[0.14em] font-medium text-black/45">
          {label}
        </span>
      </div>
      <div className="flex items-end justify-between gap-3 mt-1">
        <span
          className={`text-[22px] font-semibold tracking-tight tabular-nums leading-none ${
            show ? "text-black" : "text-black/20"
          }`}
        >
          {show ? value : "—"}
        </span>
        <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-black/35 pb-0.5">
          {unit}
        </span>
      </div>
    </div>
  );
}

function EmptyMini({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-black/40">
      <Icon className="w-6 h-6 mb-2" strokeWidth={1.5} weight="regular" />
      <div className="text-[12px]">{text}</div>
    </div>
  );
}

const TYPE_META = {
  appt: { Icon: Calendar, color: "bg-[#DBEAFE] text-[#1D4ED8]" },
  rx: { Icon: Pill, color: "bg-[#DCFCE7] text-[#15803D]" },
  dx: { Icon: FileText, color: "bg-[#FEF3C7] text-[#B45309]" },
  lab: { Icon: FlaskConical, color: "bg-[#F3E8FF] text-[#7E22CE]" },
};

function TimelineRow({ event }) {
  const meta = TYPE_META[event.type] || TYPE_META.dx;
  const { Icon } = meta;
  return (
    <div
      className="relative flex items-start gap-3"
      data-testid={`timeline-${event.id}`}
    >
      <div
        className={`absolute -left-7 top-0 w-7 h-7 rounded-full ${meta.color} flex items-center justify-center ring-4 ring-[#F5F5F7]`}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
      </div>
      <div className="flex-1 min-w-0 pl-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-black truncate">
              {event.title}
            </div>
            <div className="text-[12px] text-black/55 line-clamp-2 mt-0.5">
              {event.subtitle}
            </div>
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-black/40 whitespace-nowrap">
            {fmtDate(event.date)}
          </div>
        </div>
      </div>
    </div>
  );
}
